/**
 * Переводы для BMI Calculator (tool_id=1003)
 * UI элементы результата: статусы, сообщения, метрики
 */

export type BMICalculatorLang = 'en' | 'ru' | 'de' | 'es' | 'fr' | 'it' | 'pl' | 'lv'

export interface BMICalculatorTranslations {
    result: {
        title: string
        healthy_bmi_range: string
        healthy_weight_for_height: string
        bmi_prime: string
        ponderal_index: string
    }
    status: {
        underweight: string
        normal: string
        overweight: string
        obesity: string
    }
    messages: {
        healthy: string
        underweight: string
        overweight: string
        obese: string
    }
    errors: {
        required: string
    }
    units: {
        kg: string
        lb: string
        st: string
        cm: string
        m: string
        in: string
        ft: string
    }
    buttons: {
        save: string
        save_image: string
        copy_result: string
    }
    labels: {
        age: string
        gender: string
        height_unit: string
        height: string
        height_ft: string
        height_in: string
        weight_unit: string
        weight: string
    }
    genderOptions: {
        male: string
        female: string
    }
}

const translations: Record<BMICalculatorLang, BMICalculatorTranslations> = {
    en: {
        result: {
            title: 'Result',
            healthy_bmi_range: 'Healthy BMI range: 18.5 kg/m² - 25 kg/m²',
            healthy_weight_for_height: 'Healthy weight for the height: {min} {unit} - {max} {unit}',
            bmi_prime: 'BMI Prime: {value}',
            ponderal_index: 'Ponderal Index: {value} kg/m³'
        },
        status: {
            underweight: 'Underweight',
            normal: 'Normal',
            overweight: 'Overweight',
            obesity: 'Obesity'
        },
        messages: {
            healthy: 'Your weight is healthy.',
            underweight: 'You are underweight. You need to gain at least {weight} {unit} to reach a healthy weight.',
            overweight: 'You are overweight. You need to lose at least {weight} {unit} to reach a healthy BMI.',
            obese: 'You are obese. You need to lose at least {weight} {unit} to reach a healthy BMI.'
        },
        errors: {
            required: 'Please enter your age, height, and weight to calculate your BMI.'
        },
        units: {
            kg: 'kg',
            lb: 'lb',
            st: 'st',
            cm: 'cm',
            m: 'm',
            in: 'in',
            ft: 'ft'
        },
        buttons: {
            save: 'Save',
            save_image: 'Save as Image',
            copy_result: 'Copy result'
        },
        labels: {
            age: 'Age',
            gender: 'Gender',
            height_unit: 'Height Unit',
            height: 'Height',
            height_ft: 'Feet',
            height_in: 'Inches',
            weight_unit: 'Weight Unit',
            weight: 'Weight'
        },
        genderOptions: {
            male: 'Male',
            female: 'Female'
        }
    },
    ru: {
        result: {
            title: 'Результат',
            healthy_bmi_range: 'Здоровый диапазон ИМТ: 18.5 кг/м² - 25 кг/м²',
            healthy_weight_for_height: 'Здоровый вес для роста: {min} {unit} - {max} {unit}',
            bmi_prime: 'BMI Prime: {value}',
            ponderal_index: 'Пондеральный индекс: {value} кг/м³'
        },
        status: {
            underweight: 'Недостаточный вес',
            normal: 'Нормальный',
            overweight: 'Избыточный вес',
            obesity: 'Ожирение'
        },
        messages: {
            healthy: 'Ваш вес в норме.',
            underweight: 'У вас недостаточный вес. Вам нужно набрать как минимум {weight} {unit}, чтобы достичь здорового веса.',
            overweight: 'У вас избыточный вес. Вам нужно сбросить как минимум {weight} {unit}, чтобы достичь здорового ИМТ.',
            obese: 'У вас ожирение. Вам нужно сбросить как минимум {weight} {unit}, чтобы достичь здорового ИМТ.'
        },
        errors: {
            required: 'Пожалуйста, укажите возраст, рост и вес, чтобы рассчитать ИМТ.'
        },
        units: {
            kg: 'кг',
            lb: 'lb',
            st: 'st',
            cm: 'см',
            m: 'м',
            in: 'in',
            ft: 'ft'
        },
        buttons: {
            save: 'Сохранить',
            save_image: 'Сохранить как изображение',
            copy_result: 'Копировать результат'
        },
        labels: {
            age: 'Возраст',
            gender: 'Пол',
            height_unit: 'Единица измерения роста',
            height: 'Рост',
            height_ft: 'Футы',
            height_in: 'Дюймы',
            weight_unit: 'Единица измерения веса',
            weight: 'Вес'
        },
        genderOptions: {
            male: 'Мужской',
            female: 'Женский'
        }
    },
    de: {
        result: {
            title: 'Ergebnis',
            healthy_bmi_range: 'Gesunder BMI-Bereich: 18.5 kg/m² - 25 kg/m²',
            healthy_weight_for_height: 'Gesundes Gewicht für die Größe: {min} {unit} - {max} {unit}',
            bmi_prime: 'BMI Prime: {value}',
            ponderal_index: 'Ponderaler Index: {value} kg/m³'
        },
        status: {
            underweight: 'Untergewicht',
            normal: 'Normal',
            overweight: 'Übergewicht',
            obesity: 'Adipositas'
        },
        messages: {
            healthy: 'Ihr Gewicht ist gesund.',
            underweight: 'Sie haben Untergewicht. Sie müssen mindestens {weight} {unit} zunehmen, um ein gesundes Gewicht zu erreichen.',
            overweight: 'Sie haben Übergewicht. Sie müssen mindestens {weight} {unit} abnehmen, um einen gesunden BMI zu erreichen.',
            obese: 'Sie sind fettleibig. Sie müssen mindestens {weight} {unit} abnehmen, um einen gesunden BMI zu erreichen.'
        },
        errors: {
            required: 'Bitte geben Sie Ihr Alter, Ihre Größe und Ihr Gewicht ein, um Ihren BMI zu berechnen.'
        },
        units: {
            kg: 'kg',
            lb: 'lb',
            st: 'st',
            cm: 'cm',
            m: 'm',
            in: 'in',
            ft: 'ft'
        },
        buttons: {
            save: 'Speichern',
            save_image: 'Als Bild speichern',
            copy_result: 'Ergebnis kopieren'
        },
        labels: {
            age: 'Alter',
            gender: 'Geschlecht',
            height_unit: 'Größeneinheit',
            height: 'Größe',
            height_ft: 'Fuß',
            height_in: 'Zoll',
            weight_unit: 'Gewichtseinheit',
            weight: 'Gewicht'
        },
        genderOptions: {
            male: 'Männlich',
            female: 'Weiblich'
        }
    },
    es: {
        result: {
            title: 'Resultado',
            healthy_bmi_range: 'Rango saludable de IMC: 18.5 kg/m² - 25 kg/m²',
            healthy_weight_for_height: 'Peso saludable para la altura: {min} {unit} - {max} {unit}',
            bmi_prime: 'BMI Prime: {value}',
            ponderal_index: 'Índice Ponderal: {value} kg/m³'
        },
        status: {
            underweight: 'Bajo peso',
            normal: 'Normal',
            overweight: 'Sobrepeso',
            obesity: 'Obesidad'
        },
        messages: {
            healthy: 'Su peso es saludable.',
            underweight: 'Tiene bajo peso. Necesita ganar al menos {weight} {unit} para alcanzar un peso saludable.',
            overweight: 'Tiene sobrepeso. Necesita perder al menos {weight} {unit} para alcanzar un IMC saludable.',
            obese: 'Tiene obesidad. Necesita perder al menos {weight} {unit} para alcanzar un IMC saludable.'
        },
        errors: {
            required: 'Por favor, introduce tu edad, altura y peso para calcular tu IMC.'
        },
        units: {
            kg: 'kg',
            lb: 'lb',
            st: 'st',
            cm: 'cm',
            m: 'm',
            in: 'in',
            ft: 'ft'
        },
        buttons: {
            save: 'Guardar',
            save_image: 'Guardar como imagen',
            copy_result: 'Copiar resultado'
        },
        labels: {
            age: 'Edad',
            gender: 'Género',
            height_unit: 'Unidad de altura',
            height: 'Altura',
            height_ft: 'Pies',
            height_in: 'Pulgadas',
            weight_unit: 'Unidad de peso',
            weight: 'Peso'
        },
        genderOptions: {
            male: 'Masculino',
            female: 'Femenino'
        }
    },
    fr: {
        result: {
            title: 'Résultat',
            healthy_bmi_range: 'Plage d\'IMC saine: 18.5 kg/m² - 25 kg/m²',
            healthy_weight_for_height: 'Poids sain pour la taille: {min} {unit} - {max} {unit}',
            bmi_prime: 'BMI Prime: {value}',
            ponderal_index: 'Indice Pondéral: {value} kg/m³'
        },
        status: {
            underweight: 'Insuffisance pondérale',
            normal: 'Normal',
            overweight: 'Surpoids',
            obesity: 'Obésité'
        },
        messages: {
            healthy: 'Votre poids est sain.',
            underweight: 'Vous avez une insuffisance pondérale. Vous devez prendre au moins {weight} {unit} pour atteindre un poids sain.',
            overweight: 'Vous êtes en surpoids. Vous devez perdre au moins {weight} {unit} pour atteindre un IMC sain.',
            obese: 'Vous êtes obèse. Vous devez perdre au moins {weight} {unit} pour atteindre un IMC sain.'
        },
        errors: {
            required: 'Veuillez indiquer votre âge, votre taille et votre poids pour calculer votre IMC.'
        },
        units: {
            kg: 'kg',
            lb: 'lb',
            st: 'st',
            cm: 'cm',
            m: 'm',
            in: 'in',
            ft: 'ft'
        },
        buttons: {
            save: 'Enregistrer',
            save_image: 'Enregistrer comme image',
            copy_result: 'Copier le résultat'
        },
        labels: {
            age: 'Âge',
            gender: 'Genre',
            height_unit: 'Unité de taille',
            height: 'Taille',
            height_ft: 'Pieds',
            height_in: 'Pouces',
            weight_unit: 'Unité de poids',
            weight: 'Poids'
        },
        genderOptions: {
            male: 'Masculin',
            female: 'Féminin'
        }
    },
    it: {
        result: {
            title: 'Risultato',
            healthy_bmi_range: 'Intervallo BMI sano: 18.5 kg/m² - 25 kg/m²',
            healthy_weight_for_height: 'Peso sano per l\'altezza: {min} {unit} - {max} {unit}',
            bmi_prime: 'BMI Prime: {value}',
            ponderal_index: 'Indice Ponderal: {value} kg/m³'
        },
        status: {
            underweight: 'Sottopeso',
            normal: 'Normale',
            overweight: 'Sovrappeso',
            obesity: 'Obesità'
        },
        messages: {
            healthy: 'Il tuo peso è sano.',
            underweight: 'Sei sottopeso. Devi guadagnare almeno {weight} {unit} per raggiungere un peso sano.',
            overweight: 'Sei in sovrappeso. Devi perdere almeno {weight} {unit} per raggiungere un BMI sano.',
            obese: 'Sei obeso. Devi perdere almeno {weight} {unit} per raggiungere un BMI sano.'
        },
        errors: {
            required: 'Inserisci la tua età, altezza e peso per calcolare il tuo BMI.'
        },
        units: {
            kg: 'kg',
            lb: 'lb',
            st: 'st',
            cm: 'cm',
            m: 'm',
            in: 'in',
            ft: 'ft'
        },
        buttons: {
            save: 'Salva',
            save_image: 'Salva come immagine',
            copy_result: 'Copia risultato'
        },
        labels: {
            age: 'Età',
            gender: 'Genere',
            height_unit: 'Unità di altezza',
            height: 'Altezza',
            height_ft: 'Piedi',
            height_in: 'Pollici',
            weight_unit: 'Unità di peso',
            weight: 'Peso'
        },
        genderOptions: {
            male: 'Maschio',
            female: 'Femmina'
        }
    },
    pl: {
        result: {
            title: 'Wynik',
            healthy_bmi_range: 'Zdrowy zakres BMI: 18.5 kg/m² - 25 kg/m²',
            healthy_weight_for_height: 'Zdrowa waga dla wzrostu: {min} {unit} - {max} {unit}',
            bmi_prime: 'BMI Prime: {value}',
            ponderal_index: 'Wskaźnik Ponderalny: {value} kg/m³'
        },
        status: {
            underweight: 'Niedowaga',
            normal: 'Normalna',
            overweight: 'Nadwaga',
            obesity: 'Otyłość'
        },
        messages: {
            healthy: 'Twoja waga jest zdrowa.',
            underweight: 'Masz niedowagę. Musisz przybrać co najmniej {weight} {unit}, aby osiągnąć zdrową wagę.',
            overweight: 'Masz nadwagę. Musisz stracić co najmniej {weight} {unit}, aby osiągnąć zdrowy BMI.',
            obese: 'Masz otyłość. Musisz stracić co najmniej {weight} {unit}, aby osiągnąć zdrowy BMI.'
        },
        errors: {
            required: 'Podaj swój wiek, wzrost i wagę, aby obliczyć swoje BMI.'
        },
        units: {
            kg: 'kg',
            lb: 'lb',
            st: 'st',
            cm: 'cm',
            m: 'm',
            in: 'in',
            ft: 'ft'
        },
        buttons: {
            save: 'Zapisz',
            save_image: 'Zapisz jako obraz',
            copy_result: 'Kopiuj wynik'
        },
        labels: {
            age: 'Wiek',
            gender: 'Płeć',
            height_unit: 'Jednostka wzrostu',
            height: 'Wzrost',
            height_ft: 'Stopy',
            height_in: 'Cale',
            weight_unit: 'Jednostka wagi',
            weight: 'Waga'
        },
        genderOptions: {
            male: 'Mężczyzna',
            female: 'Kobieta'
        }
    },
    lv: {
        result: {
            title: 'Rezultāts',
            healthy_bmi_range: 'Veselīgs BMI diapazons: 18.5 kg/m² - 25 kg/m²',
            healthy_weight_for_height: 'Veselīgs svars augumam: {min} {unit} - {max} {unit}',
            bmi_prime: 'BMI Prime: {value}',
            ponderal_index: 'Ponderalais indekss: {value} kg/m³'
        },
        status: {
            underweight: 'Zems svars',
            normal: 'Normāls',
            overweight: 'Liekais svars',
            obesity: 'Aptaukošanās'
        },
        messages: {
            healthy: 'Jūsu svars ir veselīgs.',
            underweight: 'Jums ir zems svars. Jums jāpieņemas vismaz {weight} {unit}, lai sasniegtu veselīgu svaru.',
            overweight: 'Jums ir liekais svars. Jums jāzaudē vismaz {weight} {unit}, lai sasniegtu veselīgu BMI.',
            obese: 'Jums ir aptaukošanās. Jums jāzaudē vismaz {weight} {unit}, lai sasniegtu veselīgu BMI.'
        },
        errors: {
            required: 'Lūdzu, ievadiet savu vecumu, augumu un svaru, lai aprēķinātu savu ĶMI.'
        },
        units: {
            kg: 'kg',
            lb: 'lb',
            st: 'st',
            cm: 'cm',
            m: 'm',
            in: 'in',
            ft: 'ft'
        },
        buttons: {
            save: 'Saglabāt',
            save_image: 'Saglabāt kā attēlu',
            copy_result: 'Kopēt rezultātu'
        },
        labels: {
            age: 'Vecums',
            gender: 'Dzimums',
            height_unit: 'Auguma mērvienība',
            height: 'Augums',
            height_ft: 'Pēdas',
            height_in: 'Collas',
            weight_unit: 'Svara mērvienība',
            weight: 'Svars'
        },
        genderOptions: {
            male: 'Vīrietis',
            female: 'Sieviete'
        }
    }
}

export function getBMICalculatorTranslations(lang: string): BMICalculatorTranslations {
    const key = lang as BMICalculatorLang
    return translations[key] ?? translations.en
}

/**
 * Форматирует сообщение с подстановкой переменных
 */
export function formatMessage(
    template: string,
    params: Record<string, string | number>
): string {
    let result = template
    Object.entries(params).forEach(([key, value]) => {
        result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value))
    })
    return result
}
